import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiUrl } from '../config/api';
import { apiFetch } from '../utils/session';
import AuthImage from '../components/AuthImage';
import Pagination, { DEFAULT_PAGE_SIZE, pagingParams } from '../components/Pagination';
import './AdminVerify.css';

function AdminVerify() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [modalUser, setModalUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(apiUrl(`/admin/pending-users?${pagingParams(page, pageSize)}`));
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load');
      setUsers(data.users || []);
      setTotal(data.total ?? (data.users || []).length);
    } catch (e) {
      setError(e.message || 'Could not load pending users');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  const closeModal = () => setModalUser(null);

  const approve = async () => {
    if (!modalUser?._id) return;
    setActionLoading(true);
    try {
      const res = await apiFetch(apiUrl(`/admin/users/${modalUser._id}/approve`), {
        method: 'PATCH',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Approve failed');
      closeModal();
      await load();
    } catch (e) {
      setError(e.message || 'Approve failed');
    } finally {
      setActionLoading(false);
    }
  };

  const reject = async () => {
    if (!modalUser?._id) return;
    setActionLoading(true);
    try {
      const res = await apiFetch(apiUrl(`/admin/users/${modalUser._id}/reject`), {
        method: 'PATCH',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Reject failed');
      closeModal();
      await load();
    } catch (e) {
      setError(e.message || 'Reject failed');
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    if (!modalUser) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalUser]);

  return (
    <div className="verify-page">
      <div className="verify-shell">
        <header className="verify-header">
          <div>
            <h1 className="verify-title">Verification queue</h1>
            <p className="verify-sub">Compare Aadhaar with live selfie, then approve or reject.</p>
          </div>
          <div className="verify-header-actions">
            <Link className="verify-link" to="/admin">
              ← Dashboard
            </Link>
          </div>
        </header>

        {error ? (
          <p className="verify-banner-error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="verify-card">
          {loading ? (
            <p className="verify-muted">Loading…</p>
          ) : users.length === 0 ? (
            <p className="verify-muted">No pending applications.</p>
          ) : (
            <div className="verify-table-wrap">
              <table className="verify-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Area</th>
                    <th>Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr
                      key={u._id}
                      className="verify-row"
                      onClick={() => setModalUser(u)}
                      tabIndex={0}
                      role="button"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setModalUser(u);
                        }
                      }}
                    >
                      <td>{u.name}</td>
                      <td>{u.area}</td>
                      <td className="verify-ref">{u.referenceNumber || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loading ? (
            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={(n) => {
                setPageSize(n);
                setPage(1);
              }}
            />
          ) : null}
        </div>
      </div>

      {modalUser ? (
        <div
          className="verify-modal-overlay"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="verify-modal" role="dialog" aria-modal="true" aria-labelledby="verify-modal-title">
            <div className="verify-modal-head">
              <div>
                <h2 id="verify-modal-title" className="verify-modal-title">
                  {modalUser.name}
                </h2>
                <p className="verify-modal-meta">
                  {modalUser.area} · {modalUser.referenceNumber}
                </p>
              </div>
              <button type="button" className="verify-modal-close" onClick={closeModal} aria-label="Close">
                ×
              </button>
            </div>

            <div className="verify-modal-compare">
              <figure className="verify-pane">
                <figcaption>Aadhaar document</figcaption>
                <div className="verify-img-frame">
                  {modalUser.aadhaarDocumentPath ? (
                    <AuthImage path={`/admin/users/${modalUser._id}/aadhaar`} alt="Aadhaar document" />
                  ) : (
                    <span className="verify-muted">No image</span>
                  )}
                </div>
              </figure>
              <figure className="verify-pane">
                <figcaption>Live selfie</figcaption>
                <div className="verify-img-frame">
                  {modalUser.selfiePath ? (
                    <AuthImage path={`/users/${modalUser._id}/selfie`} alt="Applicant selfie" />
                  ) : (
                    <span className="verify-muted">No image</span>
                  )}
                </div>
              </figure>
            </div>

            <div className="verify-modal-actions">
              <button
                type="button"
                className="verify-btn reject"
                onClick={reject}
                disabled={actionLoading}
              >
                Reject
              </button>
              <button
                type="button"
                className="verify-btn approve"
                onClick={approve}
                disabled={actionLoading}
              >
                {actionLoading ? 'Working…' : 'Approve application'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default AdminVerify;
